/**
 * Utility helpers for grouping invoices by week and computing week starts.
 *
 * A "week" is defined as Mon–Sun. The week key is the ISO date of Monday (YYYY-MM-DD).
 */

/**
 * Returns the Monday (start of week) for a given date string (YYYY-MM-DD).
 * Uses local midnight parsing to avoid timezone rounding issues.
 */
export function getWeekStart(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const day = date.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const daysToMon = day === 0 ? 6 : day - 1;
  date.setDate(date.getDate() - daysToMon);
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/**
 * Returns a human-readable name for a payment run week.
 * e.g. "Week of Apr 28 – May 4, 2026"
 */
export function formatWeekName(mondayStr: string): string {
  const [y, m, d] = mondayStr.split("-").map(Number);
  const monday = new Date(y, m - 1, d);
  const sunday = new Date(y, m - 1, d + 6);
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const startLabel = monday.toLocaleDateString("en-US", opts);
  const endLabel = sunday.toLocaleDateString("en-US", { ...opts, year: "numeric" });
  return `Week of ${startLabel} – ${endLabel}`;
}

/**
 * Given a list of invoice rows (with early_pay_due_date), returns the unique
 * set of week-start dates (Mondays) that have at least one eligible invoice.
 *
 * An invoice is "eligible" when:
 *   - it has a non-empty early_pay_due_date
 *   - outstanding > 0 (amount_invoiced - amount_paid > 0)
 *   - potential_savings > 0
 */
export interface InvoiceForWeek {
  early_pay_due_date: string;
  amount_invoiced: number;
  amount_paid: number;
  potential_savings: number;
}

export function getWeeksWithEligibleInvoices(invoices: InvoiceForWeek[]): string[] {
  const weeks = new Set<string>();
  for (const inv of invoices) {
    if (!inv.early_pay_due_date) continue;
    if (inv.amount_invoiced - inv.amount_paid <= 0) continue;
    if (inv.potential_savings <= 0) continue;
    weeks.add(getWeekStart(inv.early_pay_due_date));
  }
  return Array.from(weeks).sort();
}

/**
 * Computes the savings-captured percentage for a past payment run.
 *
 * Numerator:   sum of potential_savings for invoices that were paid early
 *              (`paid_early` status)
 * Denominator: sum of potential_savings for ALL eligible invoices in the week
 *              (regardless of whether they were included in the payment run)
 *
 * Returns `null` when there are no eligible invoices (avoids division by zero).
 */
export interface InvoiceForCapture {
  potential_savings: number;
  paid_early: boolean;
}

export function computeSavingsCaptured(invoices: InvoiceForCapture[]): {
  capturedSavings: number;
  totalPotentialSavings: number;
  capturedPct: number | null;
} {
  const capturedSavings = invoices
    .filter((i) => i.paid_early)
    .reduce((s, i) => s + i.potential_savings, 0);
  const totalPotentialSavings = invoices.reduce((s, i) => s + i.potential_savings, 0);
  const capturedPct = totalPotentialSavings > 0 ? capturedSavings / totalPotentialSavings : null;
  return { capturedSavings, totalPotentialSavings, capturedPct };
}

/**
 * Given the weeks that have invoices and the existing runs (keyed by week_start),
 * returns the list of week-start dates that still need a run created.
 */
export function getMissingWeeks(
  weeksWithInvoices: string[],
  existingRunWeekStarts: (string | null | undefined)[]
): string[] {
  const covered = new Set(existingRunWeekStarts.filter(Boolean) as string[]);
  return weeksWithInvoices.filter((w) => !covered.has(w));
}
